/**
 * Advanced disclosure of the Azure Storage form (spec §4.1): one endpoint
 * override per service. An explicit endpoint is used verbatim by
 * `serviceEndpointFor` — it is never re-derived by string replacement — which
 * is what makes sovereign clouds, custom emulator ports and private endpoints
 * reachable without a separate suffix field.
 *
 * The endpoint-SUFFIX input of the sketch is deliberately absent: ruling O4
 * keeps national clouds out until there is real test access, and a pasted
 * non-default `EndpointSuffix` is already pinned as five explicit endpoints by
 * `addConnectionAzureStorage.ts`. Restore it here when O4 is lifted.
 */
import { useRef, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useAnalytics } from '@/core/analytics/useAnalytics';
import { cn } from '@/lib/utils';
import type { AzureStorageEndpoints } from '@/types/azureStorage';
import type { ConnectionFormValues } from './connectionFormSchema';
import { FormRow } from '../connection-dialog/pane/FormRow';
import { useRevealOnRequest } from '../connection-dialog/pane/formSectionReveal';
import type { ConnectionFormApi } from '../connection-dialog/fields/fieldControls';

/** Every endpoint slot `AzureStorageEndpoints` carries, in display order. */
const ENDPOINT_FIELDS: ReadonlyArray<{ key: keyof AzureStorageEndpoints; label: string; placeholder: string }> = [
  { key: 'blob', label: 'Blob', placeholder: 'https://contoso.blob.core.windows.net' },
  { key: 'table', label: 'Table', placeholder: 'https://contoso.table.core.windows.net' },
  { key: 'queue', label: 'Queue', placeholder: 'https://contoso.queue.core.windows.net' },
  { key: 'file', label: 'File', placeholder: 'https://contoso.file.core.windows.net' },
  { key: 'dfs', label: 'Data Lake (dfs)', placeholder: 'https://contoso.dfs.core.windows.net' },
];

interface Props {
  endpoints: AzureStorageEndpoints | undefined;
  onChange: (endpoints: AzureStorageEndpoints | undefined) => void;
}

/**
 * The Endpoints row of More options (Option C, C2; the old Advanced toggle): a disclosure that stays MOUNTED while
 * closed (rule 3b.2), and whose `ui:storage_form_advanced` event fires in both directions. It opens by itself when the
 * connection already overrides an endpoint.
 */
export function AzureStorageEndpointsRow({ form }: { form: ConnectionFormApi }) {
  const { track } = useAnalytics();
  const [open, setOpen] = useState(() => Object.keys(form.state?.values?.azureStorageEndpoints ?? {}).length > 0 && !form.state?.values?.azureStorageEmulator);
  // The field a failed test is about may be an override (C2 review C2-05): a reveal opens the disclosure first.
  const containerRef = useRef<HTMLDivElement>(null);
  useRevealOnRequest(containerRef, open, () => setOpen(true));
  return (
    <form.Subscribe selector={(state: { values: Partial<ConnectionFormValues> }) => state.values.azureStorageEndpoints}>
      {(endpoints: AzureStorageEndpoints | undefined) => (
        <FormRow row="azureStorageEndpoints" label="Endpoints" groupId="storage-endpoints-label">
          <button
            type="button"
            aria-expanded={open}
            aria-controls="storage-endpoint-fields"
            className="flex min-h-9 items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            onClick={() => {
              const next = !open;
              setOpen(next);
              track('ui:storage_form_advanced', { open: next });
            }}
            data-testid="storage-advanced-toggle"
          >
            <ChevronRight aria-hidden className={cn('h-3.5 w-3.5 transition-transform duration-150 motion-reduce:transition-none', open && 'rotate-90')} />
            Override per-service endpoints
          </button>
          <div id="storage-endpoint-fields" ref={containerRef} hidden={!open}>
            <AzureStorageEndpointFields endpoints={endpoints} onChange={(next) => form.setFieldValue('azureStorageEndpoints', next)} />
          </div>
        </FormRow>
      )}
    </form.Subscribe>
  );
}

export function AzureStorageEndpointFields({ endpoints, onChange }: Props) {
  const update = (key: keyof AzureStorageEndpoints, raw: string) => {
    const next: AzureStorageEndpoints = { ...endpoints };
    const value = raw.trim();
    if (value) next[key] = value;
    else delete next[key];
    onChange(Object.keys(next).length > 0 ? next : undefined);
  };

  return (
    <div className="mt-1 space-y-2 rounded-md border bg-muted/30 p-3">
      <p className="text-xs text-muted-foreground">
        Leave a field empty to derive it from the account name.
      </p>
      {ENDPOINT_FIELDS.map((field) => (
        <div key={field.key} className="flex items-center gap-2">
          <label
            htmlFor={`storage-endpoint-${field.key}`}
            className="w-24 shrink-0 text-xs font-medium"
          >
            {field.label}
          </label>
          <input
            id={`storage-endpoint-${field.key}`}
            type="text"
            spellCheck={false}
            placeholder={field.placeholder}
            className="block w-full rounded border border-input bg-background px-2 py-1 font-mono text-xs shadow-sm focus:border-primary focus:ring-primary"
            value={endpoints?.[field.key] ?? ''}
            onChange={(event) => update(field.key, event.target.value)}
            data-testid={`storage-endpoint-${field.key}`}
          />
        </div>
      ))}
    </div>
  );
}
